import type { FilterState, PriceBucket, SaleRecord } from './types';
import { bucketRangeLabel, escapeHtml, formatCompactCurrency } from './format';

// These limits keep the drawer readable on mobile while still preserving useful
// separation between lower-priced and high-end Rutherford County sales.
const MIN_PRICE_BUCKET_SIZE = 50_000;
const MAX_PRICE_BUCKET_COUNT = 12;

function buildPriceBucketBounds(prices: number[]): number[] {
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const firstBucketStart = Math.floor(min / MIN_PRICE_BUCKET_SIZE) * MIN_PRICE_BUCKET_SIZE;
  const lastBucketEnd = Math.max(
    firstBucketStart + MIN_PRICE_BUCKET_SIZE,
    Math.ceil(max / MIN_PRICE_BUCKET_SIZE) * MIN_PRICE_BUCKET_SIZE,
  );
  const bucketRange = lastBucketEnd - firstBucketStart;
  const rangeUnits = bucketRange / MIN_PRICE_BUCKET_SIZE;
  // Keep a fixed 50k floor, then spread buckets farther apart as the price span grows so wide markets
  // do not collapse into too many narrow bars.
  const bucketCount = Math.min(MAX_PRICE_BUCKET_COUNT, Math.max(1, Math.ceil(Math.log2(rangeUnits + 1) * 2)));
  const bounds = [firstBucketStart];

  for (let index = 1; index < bucketCount; index += 1) {
    const ratio = index / bucketCount;
    const rawBoundary = firstBucketStart + Math.expm1(Math.log1p(rangeUnits) * ratio) * MIN_PRICE_BUCKET_SIZE;
    const boundary = Math.round(rawBoundary / MIN_PRICE_BUCKET_SIZE) * MIN_PRICE_BUCKET_SIZE;
    const previous = bounds[bounds.length - 1];

    if (boundary > previous && boundary < lastBucketEnd) {
      bounds.push(boundary);
    }
  }

  // Always close the final bucket on the actual max range so the most expensive sale is not left out
  // when rounding collapses adjacent candidate boundaries.
  if (bounds[bounds.length - 1] !== lastBucketEnd) {
    bounds.push(lastBucketEnd);
  }

  return bounds;
}

function buildPriceBuckets(records: SaleRecord[]): { buckets: PriceBucket[]; validPriceCount: number; missingPriceCount: number } {
  const prices = records.map((record) => record.price).filter((price): price is number => price !== null && Number.isFinite(price));
  const validPriceCount = prices.length;
  const missingPriceCount = records.length - validPriceCount;

  if (prices.length === 0) {
    return { buckets: [], validPriceCount, missingPriceCount };
  }

  const bounds = buildPriceBucketBounds(prices);
  const buckets = bounds.slice(0, -1).map((start, index) => ({ start, end: bounds[index + 1], count: 0 }));

  for (const price of prices) {
    const bucket = buckets.find((candidate, index) => price >= candidate.start && (price < candidate.end || index === buckets.length - 1));
    if (bucket) bucket.count += 1;
  }

  return { buckets, validPriceCount, missingPriceCount };
}

function isActivePriceBucket(bucket: PriceBucket, filters: FilterState): boolean {
  return filters.minPrice === bucket.start && filters.maxPrice === bucket.end;
}

function formatAxisCurrency(value: number): string {
  if (value >= 1_000_000) {
    return `$${new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(value / 1_000_000)}M`;
  }

  return formatCompactCurrency(value);
}

function renderAxisLabel(bucket: PriceBucket): string {
  return `
    <span class="distribution-range" title="${escapeHtml(bucketRangeLabel(bucket, false))}">
      <span>${escapeHtml(formatAxisCurrency(bucket.start))}</span>
      <span>${escapeHtml(formatAxisCurrency(bucket.end))}</span>
    </span>
  `;
}

export function renderDistribution(
  records: SaleRecord[],
  filters: FilterState,
  summaryElement: HTMLElement,
  chartElement: HTMLElement,
): void {
  const { buckets, validPriceCount, missingPriceCount } = buildPriceBuckets(records);
  const missingText = missingPriceCount > 0 ? `, ${missingPriceCount.toLocaleString()} excluded with N/A price` : '';
  summaryElement.textContent = `${validPriceCount.toLocaleString()} visible sales with prices${missingText}.`;

  if (buckets.length === 0) {
    chartElement.innerHTML = '<p class="distribution-empty">No visible sales have a recorded price.</p>';
    return;
  }

  const maxCount = Math.max(...buckets.map((bucket) => bucket.count), 1);

  chartElement.innerHTML = `
    <div class="distribution-plot" style="--bucket-count: ${buckets.length}">
      <div class="distribution-y-axis" aria-hidden="true">
        <span>${maxCount.toLocaleString()}</span>
        <span>0</span>
      </div>
      <div class="distribution-bars" role="list" aria-label="Sales by log-scaled price bucket">
        ${buckets
          .map((bucket) => {
            const height = Math.max(4, (bucket.count / maxCount) * 100);
            const preciseRange = bucketRangeLabel(bucket, false);
            const isActive = isActivePriceBucket(bucket, filters);
            return `
              <button
                class="distribution-bar-item${isActive ? ' is-active' : ''}"
                type="button"
                role="listitem"
                data-price-start="${bucket.start}"
                data-price-end="${bucket.end}"
                title="${bucket.count.toLocaleString()} sales from ${escapeHtml(preciseRange)}"
                aria-pressed="${isActive}"
                aria-label="${bucket.count.toLocaleString()} sales from ${escapeHtml(preciseRange)}. Click to filter this range."
              >
                <span class="distribution-count">${bucket.count.toLocaleString()}</span>
                <span class="distribution-bar" style="--bar-height: ${height}%"></span>
              </button>
            `;
          })
          .join('')}
      </div>
      <div class="distribution-x-axis" aria-hidden="true">
        ${buckets.map(renderAxisLabel).join('')}
      </div>
    </div>
  `;
}
